import React, { useState, useCallback } from 'react';
import { Button, Upload, message, Spin } from 'antd';
import type { UploadProps } from 'antd';
import s from './s.module.less';

type Props = {
    onBack: () => void;
    onClose: () => void;
    onSelectImport: () => void;
    onSelectUpload: () => void;
    onSelectManual: () => void;
};

const GlobeIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="9.5" stroke="#0E62F4" strokeWidth="1.5" />
        <path d="M3 12H21" stroke="#0E62F4" strokeWidth="1.5" strokeLinecap="round" />
        <ellipse cx="12" cy="12" rx="4" ry="9.5" stroke="#0E62F4" strokeWidth="1.5" />
    </svg>
);

const TableIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3.5" y="3.5" width="17" height="17" rx="1.5" stroke="#0E62F4" strokeWidth="1.5" />
        <path d="M3.5 9H20.5" stroke="#0E62F4" strokeWidth="1.5" />
        <path d="M3.5 15H20.5" stroke="#0E62F4" strokeWidth="1.5" />
        <path d="M9 3.5V20.5" stroke="#0E62F4" strokeWidth="1.5" />
    </svg>
);

const HandIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M18 11V6.5C18 5.67157 17.3284 5 16.5 5C15.6716 5 15 5.67157 15 6.5"
            stroke="#0E62F4"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M15 10.5V4.5C15 3.67157 14.3284 3 13.5 3C12.6716 3 12 3.67157 12 4.5"
            stroke="#0E62F4"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M12 10.5V3.5C12 2.67157 11.3284 2 10.5 2C9.67157 2 9 2.67157 9 3.5V10.5"
            stroke="#0E62F4"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M18 8.5C18 7.67157 18.6716 7 19.5 7C20.3284 7 21 7.67157 21 8.5V14C21 18.4183 17.4183 22 13 22H11.9999C9.20729 22 7.59079 21.1517 6.10389 19.6741L2.51389 16.0741C1.73285 15.293 1.73285 14.027 2.51389 13.2459C3.29494 12.4649 4.56127 12.4649 5.34232 13.2459L7 14.9036V6.5C7 5.67157 7.67157 5 8.5 5"
            stroke="#0E62F4"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const UploadIcon = () => (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M28 8H12C10.8954 8 10 8.89543 10 10V38C10 39.1046 10.8954 40 12 40H36C37.1046 40 38 39.1046 38 38V18L28 8Z"
            stroke="#9CA3AF"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="M28 8V18H38" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M24 34V24" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M19 29L24 24L29 29" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CreateProfilesStep: React.FC<Props> = ({ onBack, onSelectImport, onSelectUpload, onSelectManual }) => {
    const [loading, setLoading] = useState(false);
    const [uploadedFile, setUploadedFile] = useState<string | null>(null);

    const uploadProps: UploadProps = {
        name: 'file',
        showUploadList: false,
        multiple: false,
        accept: '.pdf',
        beforeUpload(file) {
            const isLt10M = file.size / 1024 / 1024 <= 10;
            if (!isLt10M) {
                message.error('File must be smaller than 10MB');
                return false;
            }
            return true;
        },
        customRequest: async (data) => {
            setLoading(true);
            try {
                const formData = new FormData();
                formData.append('file', data.file);
                // TODO: call upload API
                setUploadedFile((data.file as File).name);
                message.success('File uploaded successfully');
            } catch (e) {
                console.error(e);
                message.error('Upload failed');
            } finally {
                setLoading(false);
            }
        },
    };

    return (
        <>
            <div className={s.body}>
                <h3 className={s.sectionTitle}>How would you like to add provider information?</h3>
                <div className={s.importCards}>
                    <div className={s.importCard} onClick={onSelectImport}>
                        <div className={s.importIconWrap}>
                            <GlobeIcon />
                        </div>
                        <div className={s.importCardContent}>
                            <div className={s.importCardTitle}>Import from website</div>
                            <div className={s.importCardDesc}>
                                {`We’ll scan your practice website to find providers and prefill profile details like name, specialty, and photo`}
                            </div>
                        </div>
                    </div>
                    <div className={s.importCard} onClick={onSelectManual}>
                        <div className={s.importIconWrap}>
                            <HandIcon />
                        </div>
                        <div className={s.importCardContent}>
                            <div className={s.importCardTitle}>Manual entry</div>
                            <div className={s.importCardDesc}>
                                Enter provider details yourself to create profiles one at a time.
                            </div>
                        </div>
                    </div>
                    <div className={s.importCard} onClick={onSelectUpload}>
                        <div className={s.importIconWrap}>
                            <TableIcon />
                        </div>
                        <div className={s.importCardContent}>
                            <div className={s.importCardTitle}>Direct upload</div>
                            <div className={s.importCardDesc}>
                                Upload a XLSX file with provider details to create profiles in bulk
                            </div>
                        </div>
                    </div>
                </div>
                {/* <div className={s.uploadSection}>
                    {loading ? (
                        <div className={s.loadingMask}>
                            <Spin />
                        </div>
                    ) : (
                        <Upload.Dragger {...uploadProps} className={s.uploadDragger}>
                            <div className={s.uploadContent}>
                                <UploadIcon />
                                <p className={s.uploadText}>
                                    <span className={s.uploadLink}>Upload resumes, CVs, or documents</span> or drag and
                                    drop
                                </p>
                                <p className={s.uploadHint}>.PDF up to 10MB</p>
                            </div>
                        </Upload.Dragger>
                    )}
                </div> */}
            </div>
            <div className={s.footer}>
                <div className={s.footerBorder} />
                <div className={s.footerActions}>
                    <Button className={s.backBtn} onClick={onBack}>
                        Back
                    </Button>
                    <Button type="primary" disabled className={s.disabledBtn}>
                        Next
                    </Button>
                </div>
            </div>
        </>
    );
};

export default CreateProfilesStep;
